import * as React from 'react';

import logoDark from '~/assets/logo-dark.svg';
import logoLight from '~/assets/logo-light.svg';
import {
  ChartNoAxesCombined,
  Settings2,
  MessageSquare,
  ChevronRight,
  ArrowLeft,
} from 'lucide-react';

import { NavMain } from '~/components/NavMain';
import { NavUser } from '~/components/NavUser';
import { TeamSwitcher } from '~/components/TeamSwitcher';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
} from '~/components/ui/Sidebar';

import { useNavigate, useLocation } from 'react-router';
import { useIsMobile } from '~/hooks/use-mobile';
import ChatSidebarContent from '~/components/ai-chatbot/ChatSidebarContent';
import { useTheme } from '~/contexts/ThemeContext';
import { useActiveTeam } from '~/stores/useTeamStore';
import { useAuthStore } from '~/stores/useAuthStore';

export function AppSidebar(props: React.ComponentProps<typeof Sidebar>) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { theme } = useTheme();
  const activeTeam = useActiveTeam();
  const isSuperAdmin = useAuthStore((s) => !!s.user?.isSuperAdmin);

  const navMain = React.useMemo(() => {
    const dashboards = [{ title: 'Overview', url: '/dashboard' }];
    dashboards.push(
      { title: 'Paid Social', url: '/dashboard/paid-social' },
      { title: 'Paid Search', url: '/dashboard/paid-search' },
      { title: 'Organic Social', url: '/dashboard/organic-social' },
      { title: 'Organic Search', url: '/dashboard/organic-search' },
      { title: 'Marketplace', url: '/dashboard/marketplace' },
      { title: 'QComm Shelf', url: '/dashboard/qcomm-shelf' },
      { title: 'QComm Trends', url: '/dashboard/qcomm-trends' },
      { title: 'App Analytics', url: '/dashboard/app-analytics' },
      { title: 'Competitor', url: '/dashboard/competitor' },
      { title: 'Sales', url: '/dashboard/sales' },
    );
    if (activeTeam?.brandProfile?.hasStorePerformance) {
      dashboards.push({
        title: 'Store Performance',
        url: '/dashboard/store-performance',
      });
    }
    if (activeTeam?.brandProfile?.hasBlinkitBrands) {
      dashboards.push({
        title: 'Blinkit Brands',
        url: '/dashboard/blinkit-brands',
      });
    }
    if (activeTeam?.brandProfile?.hasChannelPerformance) {
      dashboards.push({
        title: 'Channel Insights',
        url: '/dashboard/channel-performance',
      });
    }
    if (activeTeam?.brandProfile?.hasLeadsTracking)
      dashboards.push({
        title: 'Leads Tracking',
        url: '/dashboard/leads-tracking',
      });
    if (activeTeam?.brandProfile?.hasPerformanceTracking)
      dashboards.push({
        title: 'Performance',
        url: '/dashboard/performance-tracking',
      });
    const allRoutes = [
      {
        title: 'Dashboards',
        url: '/dashboard',
        icon: ChartNoAxesCombined,
        isActive: true,
        items: dashboards,
      },
    ];

    if (activeTeam?.role == 1 || activeTeam?.role == 2) {
      allRoutes.push({
        title: 'Team Settings',
        url: '/settings/team',
        icon: Settings2,
        isActive: true,
        items: [
          { title: 'Brand Profile', url: '/team/brand-profile' },
          { title: 'Connections', url: '/team/connections' },
          { title: 'Manage Members', url: '/team/manage' },
        ],
      });
    }

    if (isSuperAdmin) {
      allRoutes.push({
        title: 'Superadmin',
        url: '/team/locations',
        icon: Settings2,
        isActive: true,
        items: [{ title: 'Locations', url: '/team/locations' }],
      });
    }

    return allRoutes;
  }, [activeTeam, isSuperAdmin]);

  const isOnChatRoute = location.pathname.startsWith('/chat');

  const [mobileView, setMobileView] = React.useState<'navigation' | 'chat'>(
    isOnChatRoute ? 'chat' : 'navigation',
  );

  React.useEffect(() => {
    if (isMobile) {
      setMobileView(isOnChatRoute ? 'chat' : 'navigation');
    }
  }, [isMobile, isOnChatRoute]);

  const handleChatButtonClick = () => {
    if (isMobile) {
      setMobileView((v) => (v === 'navigation' ? 'chat' : 'navigation'));
    } else {
      navigate('/chat');
    }
  };

  const showingChatOnMobile = isMobile && mobileView === 'chat';

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem
            className={`
              transition-all duration-300 ease-in-out overflow-hidden
              ${
                showingChatOnMobile
                  ? 'max-h-0 opacity-0'
                  : 'max-h-[500px] opacity-100'
              }
            `}
          >
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-gray-100 rounded-none dark:data-[state=open]:bg-gray-800"
              onClick={() => navigate('/dashboard')}
            >
              <img
                src={theme === 'dark' ? logoLight : logoDark}
                alt="DViO One logo"
                className="object-contain h-8"
              />
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarSeparator />
          <TeamSwitcher />
          <SidebarSeparator />
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              className={`
                data-[state=open]:bg-gray-100 dark:data-[state=open]:bg-gray-800
                transition-all duration-200
                ${
                  isOnChatRoute && !showingChatOnMobile
                    ? 'bg-linear-to-r from-primary/10 to-primary/5 shadow-sm'
                    : ''
                }
              `}
              onClick={handleChatButtonClick}
            >
              <div className="flex aspect-square size-8 items-center justify-center rounded-lg">
                {showingChatOnMobile ? (
                  <ArrowLeft className="size-4 text-foreground" />
                ) : (
                  <MessageSquare className="size-4 text-foreground" />
                )}
              </div>
              <div className="grid flex-1 text-left text-sm font-medium leading-tight">
                {showingChatOnMobile
                  ? 'Dashboard'
                  : isOnChatRoute
                    ? 'Chat History'
                    : 'Chat with AI'}
              </div>
              {!isMobile && (
                <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarSeparator />
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent className="relative overflow-hidden">
        <div
          className={`
            absolute inset-0 w-full
            transition-transform duration-300 ease-in-out
            overflow-y-auto overflow-x-hidden
            [&::-webkit-scrollbar]:w-1.5
            [&::-webkit-scrollbar-thumb]:rounded-full
            [&::-webkit-scrollbar-track]:bg-transparent
            ${showingChatOnMobile ? '-translate-x-full' : 'translate-x-0'}
          `}
        >
          <NavMain items={navMain} />
        </div>

        {(isOnChatRoute || showingChatOnMobile) && (
          <div
            className={`
              absolute inset-0 w-full h-[calc(100vh-64px)]
              transition-transform duration-300 ease-in-out
              ${showingChatOnMobile ? 'translate-x-0' : 'translate-x-full'}
            `}
            inert={!showingChatOnMobile || undefined}
          >
            <ChatSidebarContent />
          </div>
        )}
      </SidebarContent>

      <div
        className={`
          transition-all duration-300 ease-in-out overflow-hidden
          ${
            showingChatOnMobile
              ? 'max-h-0 opacity-0'
              : 'max-h-[500px] opacity-100'
          }
        `}
      >
        <SidebarFooter>
          <NavUser />
        </SidebarFooter>
      </div>

      <SidebarRail />
    </Sidebar>
  );
}
